import { z } from 'zod'

import { m } from '@/lib/paraglide/messages'

export const AGRI_IN_REASONS = ['from_supplier', 'adjustment'] as const
export const AGRI_OUT_REASONS = ['for_use', 'discard', 'adjustment'] as const

export type AgriInNoteFormReason = (typeof AGRI_IN_REASONS)[number]
export type AgriOutNoteFormReason = (typeof AGRI_OUT_REASONS)[number]

const agriInReasonSet: ReadonlySet<string> = new Set(AGRI_IN_REASONS)
const agriOutReasonSet: ReadonlySet<string> = new Set(AGRI_OUT_REASONS)

export function isAgriInNoteFormReason(
    reason: string,
): reason is AgriInNoteFormReason {
    return agriInReasonSet.has(reason)
}

export function isAgriOutNoteFormReason(
    reason: string,
): reason is AgriOutNoteFormReason {
    return agriOutReasonSet.has(reason)
}

/** Schema tạo phiếu nhập kho (PNK) vật tư nông nghiệp — dùng LotItemsField
 *  (mỗi dòng kind='new' tạo lô mới HOẶC kind='existing' chọn lô có sẵn). Factory
 *  để i18n lazy. */
export function createAgriInNoteSchema() {
    const qty = z
        .number({ message: m.aml_val_quantity_positive() })
        .positive(m.aml_val_quantity_positive())
    const item = z.discriminatedUnion('kind', [
        z.object({
            kind: z.literal('new'),
            new_lot_material_id: z
                .string()
                .min(1, m.aml_val_material_required()),
            new_lot_code: z.string().optional(),
            new_lot_supplier_name: z.string().optional(),
            new_lot_manufactured_date: z.string().optional(),
            new_lot_expiry_date: z.string().optional(),
            quantity: qty,
        }),
        z.object({
            kind: z.literal('existing'),
            agri_material_lot_id: z.string().min(1, m.aml_val_lot_required()),
            // Nhãn lô để hiển thị khi sửa (lô nằm ngoài trang search của picker).
            lot_code: z.string().optional(),
            material_name: z.string().optional(),
            // Đơn vị (enum BE) để seed hậu tố ô số lượng khi sửa; không gửi lên BE.
            material_unit: z.string().optional(),
            quantity: qty,
        }),
    ])
    return z.object({
        reason: z.enum(AGRI_IN_REASONS, {
            message: m.aml_val_reason_required(),
        }),
        notes: z.string().optional(),
        items: z.array(item).min(1, m.aml_val_items_required()),
    })
}

/** Schema tạo/sửa phiếu xuất kho (PXK) vật tư nông nghiệp. Mỗi dòng chỉ chọn
 *  lô để xuất — lô thường HOẶC lô ảo 'STOCK' của vật tư qty-mode (BE tự giải lô
 *  ảo). Hộ trồng tùy chọn (chỉ có nghĩa khi lý do = xuất sử dụng). Dùng chung
 *  cho cả màn tạo và sửa (chỉ khác `mode`). */
export function createAgriOutNoteSchema() {
    const qty = z
        .number({ message: m.aml_val_quantity_positive() })
        .positive(m.aml_val_quantity_positive())
    return z.object({
        reason: z.enum(AGRI_OUT_REASONS, {
            message: m.aml_val_reason_required(),
        }),
        notes: z.string().optional(),
        items: z
            .array(
                z.object({
                    // `kind` do LotItemsField gán ('existing'); không bắt buộc
                    // để payload tối giản vẫn hợp lệ.
                    kind: z.literal('existing').optional(),
                    agri_material_lot_id: z
                        .string()
                        .min(1, m.aml_val_lot_required()),
                    grower_id: z.string().optional(),
                    grower_name: z.string().optional(),
                    quantity: qty,
                }),
            )
            .min(1, m.aml_val_items_required()),
    })
}

export type AgriInNoteFormValues = z.infer<
    ReturnType<typeof createAgriInNoteSchema>
>
export type AgriOutNoteFormValues = z.infer<
    ReturnType<typeof createAgriOutNoteSchema>
>
